import { ListItem } from "@solomon-apps/shared/src/components/Data/List";
import { IndicationUnit } from "@solomon-apps/shared/src/components/Metrics/IndicationUnit";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import React from "react";

import type { Program } from "../../shared/programMocks";
import { regionLabelOf, STATUS_STATE } from "../programHelpers";

export interface ProgramRowProps {
    program: Program;
}

/**
 * ProgramRow — one program in the list rail. A Domain Component that wraps
 * the `ListItem` GC (mirrors PolicyRow / AddressRow / FundingRow): a status
 * dot + program name on the top line, the region on a sub-line. Selection
 * (highlight, click) is owned by the enclosing `List` GC via `itemKey`.
 *
 * @see ProgramRow.intent.md   policy
 * @see ProgramRow.layout.html intended layout
 */
export function ProgramRow({ program }: ProgramRowProps): React.ReactElement {
    return (
        <ListItem itemKey={program.id} padding="px-4 py-3" gap="gap-0">
            <div className="min-w-0">
                <div className="flex items-start gap-2">
                    {/* Dot boxed to the first line's height so it stays centered
                        on line 1 when the name wraps to two lines. */}
                    <span className="flex h-5 shrink-0 items-center md:h-6">
                        <IndicationUnit
                            variant="status"
                            state={STATUS_STATE[program.status]}
                            size={7}
                        />
                    </span>
                    <Text
                        variant="small"
                        render={<span />}
                        className="min-w-0 text-foreground-base break-words"
                    >
                        {program.name}
                    </Text>
                </div>
                <Text
                    variant="fine-print"
                    className="text-foreground-muted mt-1 pl-4"
                >
                    {regionLabelOf(program.region)}
                </Text>
            </div>
        </ListItem>
    );
}
